import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getAdmissions, getAdmissionStudents } from '../academyApi';
import { formatRate } from '../academyModel';
import { AcademyButton, DateInput } from '../controls';
import { daysBetween, todayKST } from '../periodDates';
import { Box, ErrorNotice, NameLink, Spinner, ToneBadge } from '../ui';
import { displayNumber, ReportTable, useReport } from './ReportContent';
import { admissionPeriodRange, validDate } from './reportModel';
import { AdmissionRateChart } from './RateCharts';

const STATUS_LABELS = { all: '등록 전체', admitted: '입학', not_admitted: '미입학' };
const LIST_RESET = { admission_status: null, list_from: null, list_to: null, admission_page: null };
const dateTime = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const when = (value) => value ? dateTime.format(new Date(value)) : '편성 없음';

function AppliedRange({ requestedFrom, from, to }) {
  if (!validDate(requestedFrom) || requestedFrom >= from) return null;
  return <p role="status" className="text-xs text-gray-500">
    선택 기간 {requestedFrom} ~ {to} 중 {from}부터 집계합니다. 이전 기간은 자료가 없어 제외합니다.
  </p>;
}

export function admissionRangeError({ from, to, asOf }, today = todayKST()) {
  if (![from, to, asOf].every(validDate)) return '등록 기간과 조회 기준일을 1900~9998년의 날짜로 입력해 주세요.';
  if (from > to) return '등록 종료일은 시작일과 같거나 뒤여야 합니다.';
  if (asOf > today) return '입학 판단 기준일은 오늘까지 선택할 수 있습니다.';
  if (daysBetween(from, to) + 1 > 3653) return '등록 기간은 3653일 이내로 선택해 주세요.';
  return null;
}

function Coverage({ value, compact = false }) {
  if (compact && value.status !== 'partial' && !(value.excluded_count > 0)) return null;
  return <div className="space-y-1 text-xs text-gray-500">
    {!compact && <p>집계 자료 시작일 {value.data_reliable_from} · 테스트 학생 제외</p>}
    {value.status === 'partial' && <p role="status" className="text-yellow-800">최초 생성 시각을 알 수 없는 학생이 전체 {displayNumber(value.unknown_created_at_total)}명 있습니다. 이 학생들은 기간을 정할 수 없어 집계에서 제외했습니다.</p>}
    {value.excluded_count > 0 && <p className="text-yellow-800">선택한 기간에서 제외된 학생은 {displayNumber(value.excluded_count)}명입니다.</p>}
  </div>;
}

export function AdmissionsError({ error, onRetry, onAdjust }) {
  const start = error.data?.data_reliable_from;
  if (error.httpStatus === 400 && error.reason === 'before_data_reliable_from' && validDate(start)) {
    const [year, month, day] = start.split('-').map(Number);
    return <div role="status" className="space-y-2 text-xs text-gray-600">
      <p>입학률은 {year}년 {month}월 {day}일부터 볼 수 있습니다.</p>
      {onAdjust && <AcademyButton onClick={() => onAdjust(start)}>가능한 시작일로 조회</AcademyButton>}
    </div>;
  }
  if (error.httpStatus === 409 && error.reason === 'not_ready') return <p role="status" className="text-xs text-gray-600">학생 v2 전환 후 입학률을 볼 수 있습니다.</p>;
  if (error.httpStatus === 403) return <p role="alert" className="text-xs text-danger">입학률은 매니저만 볼 수 있습니다.</p>;
  return <ErrorNotice error={error} onRetry={onRetry} compact />;
}

/** 요약은 서버의 자료 시작일부터 조회하고 실제 집계 기간을 표시한다. 상세 직접 입력은 바꾸지 않는다. */
async function loadDefaultSummary(range, getAccessToken) {
  try { return await getAdmissions(range, getAccessToken); } catch (error) {
    const start = error.data?.data_reliable_from;
    if (error.httpStatus === 400 && error.reason === 'before_data_reliable_from' && validDate(start) && start > range.from && start <= range.to) {
      return getAdmissions({ ...range, from: start }, getAccessToken);
    }
    throw error;
  }
}

export function AdmissionsSummary({ period: selectedPeriod, onOpenReport }) {
  const { getAccessToken } = useAuth();
  const today = todayKST();
  const { from, to, asOf } = admissionPeriodRange(selectedPeriod, today);
  const result = useReport(async (signal) => ({
    ...await loadDefaultSummary({ from, to, asOf, signal }, getAccessToken), requestedFrom: from,
  }), `admissions-summary-${from}-${to}-${asOf}`, { keepPreviousData: true });
  const d = result.data;
  const open = () => { if (!result.refreshing) onOpenReport?.('admissions', { from: d.from, to: d.to, as_of: d.as_of, requested_from: d.requestedFrom < d.from ? d.requestedFrom : null }); };
  return <div aria-busy={result.refreshing} className="academy-rate-summary flex h-full min-w-0 flex-col gap-1">
    {result.error ? <AdmissionsError error={result.error} onRetry={result.reload} /> : !d ? <Spinner label="입학률 불러오는 중" compact /> : <>
      <div className="flex min-h-6 flex-wrap items-center justify-between gap-x-2">
        <p className="text-xs text-gray-500"><span className="whitespace-nowrap">{d.from}</span> ~ <span className="whitespace-nowrap">{d.to}</span></p>
        {onOpenReport && <button type="button" aria-label="입학률 자세히" disabled={result.refreshing} onClick={open} className="inline-flex min-h-6 min-w-9 items-center justify-end text-xs text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{result.refreshing ? <span role="status" aria-label="입학률 조회 중입니다. 이전 결과를 표시합니다." className="inline-flex min-w-9 justify-center"><Loader2 aria-hidden="true" className="size-3.5 animate-spin" /></span> : '자세히'}</button>}
      </div>
      <AdmissionRateChart total={d.total} onSelect={onOpenReport ? open : undefined} disabled={result.refreshing} compact />
      <Coverage value={d.coverage} compact />
    </>}
  </div>;
}

function AdmissionDates({ range, onApply }) {
  const [draft, setDraft] = useState(range);
  const error = admissionRangeError(draft);
  return <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (!error) onApply(draft); }}>
    <div className="flex flex-wrap items-end gap-2">
      {[['from', '등록 시작일'], ['to', '등록 종료일'], ['asOf', '입학 판단 기준일']].map(([key, label]) => <label key={key} className="min-w-0 space-y-1 text-xs text-gray-500">
        <span className="block">{label}</span>
        <DateInput aria-label={label} value={draft[key]} min="1900-01-01" max={key === 'asOf' ? todayKST() : '9998-12-31'} onChange={(value) => setDraft({ ...draft, [key]: value })} compact fullWidth={false} className="w-36" />
      </label>)}
      <AcademyButton type="submit" disabled={!!error}>입학률 조회</AcademyButton>
    </div>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
  </form>;
}

function StudentList({ range, status, page, onPage, onOpenStudent, onAdjust }) {
  const { getAccessToken } = useAuth();
  const result = useReport((signal) => getAdmissionStudents({ ...range, admissionStatus: status, page, perPage: 50, signal }, getAccessToken), `admission-list-${range.from}-${range.to}-${range.asOf}-${status}-${page}`);
  const d = result.data;
  return <Box title={`${STATUS_LABELS[status]} 학생 명단`} sub={`${range.from} ~ ${range.to} 유입 · ${range.asOf} 기준`}>
    {result.error ? <AdmissionsError error={result.error} onRetry={result.reload} onAdjust={onAdjust} /> : !d ? <Spinner label="입학률 학생 명단 불러오는 중" /> : <div className="space-y-3">
      <p className="text-xs text-gray-500">총 {displayNumber(d.total_items)}명 · 시각은 한국 시간입니다. 편성 확정 시각이 기준일 뒤이면 미입학으로 표시합니다.</p>
      <Coverage value={d.coverage} />
      {d.items.length ? <ReportTable label="입학률 학생 명단" headings={['학생 · ID', '최초 생성 시각', '첫 유효 편성 확정 시각', '입학 상태']}>
        {d.items.map((student) => <tr key={student.student_id}>
          <th scope="row" className="p-2 text-left font-normal"><NameLink id={student.student_id} name={student.student_name} onOpen={onOpenStudent} /><span className="ml-1 text-gray-500">#{student.student_id}</span>{student.is_deleted && <span className="ml-1"><ToneBadge>삭제된 계정</ToneBadge></span>}</th>
          <td className="p-2 text-right">{when(student.created_at)}</td>
          <td className="p-2 text-right">{when(student.first_enrollment_confirmed_at)}</td>
          <td className="p-2 text-right">{STATUS_LABELS[student.admission_status]}</td>
        </tr>)}
      </ReportTable> : <p className="text-xs text-gray-500">이 페이지에 해당하는 학생이 없습니다.</p>}
      <nav aria-label="입학률 명단 페이지" className="flex flex-wrap items-center justify-end gap-2 text-xs text-gray-500">
        <AcademyButton disabled={page <= 1} onClick={() => onPage(Math.max(1, Math.min(page - 1, d.total_pages)))}>이전</AcademyButton>
        <span>{page} / {Math.max(1, d.total_pages)}쪽</span>
        <AcademyButton disabled={page >= d.total_pages} onClick={() => onPage(page + 1)}>다음</AcademyButton>
      </nav>
    </div>}
  </Box>;
}

function AdmissionResults({ range, filters, onChange, onOpenStudent, onAdjust }) {
  const { getAccessToken } = useAuth();
  const result = useReport((signal) => getAdmissions({ ...range, signal }, getAccessToken), `admissions-${range.from}-${range.to}-${range.asOf}`);
  if (result.error) return <AdmissionsError error={result.error} onRetry={result.reload} onAdjust={onAdjust} />;
  if (!result.data) return <Spinner label="입학률 불러오는 중" />;
  const d = result.data;
  const show = (status, from, to) => onChange({ admission_status: status, list_from: from, list_to: to, admission_page: null });
  const cells = (row, from, to, label) => <>
    {[['consulted', 'all'], ['admitted', 'admitted'], ['not_admitted', 'not_admitted']].map(([key, status]) => <td key={key} className="p-2 text-right">
      <button type="button" aria-label={`${label} ${STATUS_LABELS[status]} ${row[key]}명 보기`} onClick={() => show(status, from, to)} className="min-h-8 min-w-8 text-blue-700 underline decoration-blue-200 underline-offset-4 focus-visible:ring-2 focus-visible:ring-primary">{displayNumber(row[key])}</button>
    </td>)}
    <td className="p-2 text-right tabular-nums">{formatRate(row.admission_rate)}</td>
  </>;
  const listRange = { from: filters.list_from ?? d.from, to: filters.list_to ?? d.to, asOf: d.as_of };
  const status = filters.admission_status;
  const listValid = Object.hasOwn(STATUS_LABELS, status) && !admissionRangeError(listRange) && listRange.from >= d.from && listRange.to <= d.to;
  const page = /^\d+$/.test(filters.admission_page || '') && Number.isSafeInteger(Number(filters.admission_page)) ? Math.max(1, Number(filters.admission_page)) : 1;
  return <>
    <p className="text-xs text-gray-500">등록 {d.from} ~ {d.to} · 입학 판단 {d.as_of} 기준</p>
    <AppliedRange requestedFrom={filters.requested_from} from={d.from} to={d.to} />
    <Coverage value={d.coverage} />
    <AdmissionRateChart total={d.total} onSelect={(status) => show(status, d.from, d.to)} />
    <ReportTable label="등록 월별 입학률" headings={['등록 월', '등록', '입학', '미입학', '입학률']}>
      <tr className="bg-gray-50 font-semibold"><th scope="row" className="p-2 text-left">전체</th>{cells(d.total, d.from, d.to, '전체')}</tr>
      {d.buckets.map((row) => <tr key={row.month}><th scope="row" className="p-2 text-left font-normal">{row.month}{row.partial && <span className="block text-[11px] text-gray-500">{row.from} ~ {row.to}</span>}</th>{cells(row, row.from, row.to, row.month)}</tr>)}
    </ReportTable>
    <p className="text-xs text-gray-500">숫자를 누르면 학생 명단이 열립니다. 전체 입학률은 전체 입학 수를 전체 등록 수로 나눈 값입니다.</p>
    {status && !listValid && <p role="alert" className="text-xs text-danger">명단 조회 조건이 맞지 않습니다. 표의 숫자를 다시 선택해 주세요.</p>}
    {listValid && <StudentList range={listRange} status={status} page={page} onPage={(value) => onChange({ admission_page: String(value) })} onOpenStudent={onOpenStudent} onAdjust={onAdjust} />}
  </>;
}

export default function AdmissionsReport({ filters, onChange, onOpenStudent }) {
  const today = todayKST();
  const range = { from: filters.from ?? `${today.slice(0, 7)}-01`, to: filters.to ?? today, asOf: filters.as_of ?? today };
  const error = admissionRangeError(range);
  const apply = ({ from, to, asOf }) => onChange({ from, to, as_of: asOf, requested_from: null, ...LIST_RESET });
  const adjust = (start) => apply({ ...range, from: start, to: validDate(range.to) && range.to >= start ? range.to : start });
  return <>
    <p className="text-xs leading-relaxed text-gray-600">등록일은 학생 정보를 처음 만든 날입니다. 입학은 첫 유효 반 편성을 저장한 시각으로 판단합니다. 실제 입반일과는 다릅니다. 편성을 취소하면 다시 계산하며, 입학 후 휴원·퇴원한 학생은 실적에 남습니다.</p>
    <AdmissionDates key={`${range.from}-${range.to}-${range.asOf}`} range={range} onApply={apply} />
    {!error && <AdmissionResults range={range} filters={filters} onChange={onChange} onOpenStudent={onOpenStudent} onAdjust={adjust} />}
  </>;
}
